import { useCallback, useEffect, useRef, useState } from 'react';

interface UseWebWorkerReturn {
  result: unknown;
  error: string | null;
  isLoading: boolean;
  runTask: (data: unknown) => void;
}

export const useWebWorker = (workerScript: string | URL): UseWebWorkerReturn => {
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    // Create worker
    workerRef.current = new Worker(workerScript, { type: 'module' });

    // Handle successful messages
    workerRef.current.onmessage = (event: MessageEvent) => {
      setResult(event.data);
      setError(null);
      setIsLoading(false);
    };

    // Handle errors
    workerRef.current.onerror = (event: ErrorEvent) => {
      setError(event.message || 'Worker error occurred');
      setIsLoading(false);
    };

    // Handle message errors
    workerRef.current.onmessageerror = () => {
      setError('Failed to deserialize message from worker');
      setIsLoading(false);
    };

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [workerScript]);

  const runTask = useCallback((data: unknown) => {
    if (!workerRef.current) {
      setError('Worker is not available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      workerRef.current.postMessage(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message to worker');
      setIsLoading(false);
    }
  }, []);

  return { result, error, isLoading, runTask };
};
